import { useMemo } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb'
import { BlogSidebar } from '@/components/website/BlogSidebar'
import { Seo } from '@/components/seo/Seo'
import { DETAIL_GALLERY, DETAIL_IMAGE } from '@/data/blog'
import { site } from '@/data/site'
import { useWebsiteBlog, useWebsiteBlogs } from '@/services/httpServices/queries/useWebsite'

const FILLER_A =
  'Good health is built on everyday habits — balanced food, regular movement, enough sleep and timely medical checkups. At Fast Aid Hospital we help you understand your body and act early.'

const FILLER_B =
  'Many serious conditions, including diabetes, high blood pressure and heart disease, develop silently over years. Regular screening allows treatment to begin before complications appear.'

const FILLER_C =
  'Our doctors take the time to explain your diagnosis and treatment in simple language, and our diagnostic services provide same-day reports for most routine tests.'

const FILLER_D =
  'Prevention is always better than cure. A yearly health checkup, up-to-date vaccinations and prompt attention to warning signs protect you and your family.'

const FILLER_E =
  'Fast Aid Hospital, Chauddagram is open 24/7 for emergencies and offers outdoor consultation, diagnostics and specialist care under one roof.'

const FILLER_F =
  'Whether you need emergency care, a routine checkup or management of a long-term condition, our team is here to support you with compassionate, affordable care.'

const FILLER_G =
  'Call 09610009620 to book an appointment or visit us in Chauddagram, Chittagong. Your health is our priority.'

const CHECKLIST = [
  'Experienced doctors across multiple specialties',
  '24/7 emergency and ambulance support',
  'Same-day laboratory and imaging reports',
  'Affordable, transparent treatment costs',
]

function ShareIcon({ label, icon }: { label: string; icon: string }) {
  return (
    <li>
      <a href="#" aria-label={label}>
        <i className={icon}></i>
      </a>
    </li>
  )
}

export function BlogDetail() {
  const { slug } = useParams()
  const { post, isLoading } = useWebsiteBlog(slug)
  const { posts } = useWebsiteBlogs()

  // Stable `dangerouslySetInnerHTML` prop (React compares it by reference), so
  // re-renders don't reset the injected markup and undo template init.
  const richHtml = useMemo(() => ({ __html: (post?.content ?? '').trim() }), [post?.content])

  if (!post) {
    if (isLoading) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center">
          <i className="fa-solid fa-circle-notch fa-spin text-3xl text-primary" aria-label="Loading" />
        </div>
      )
    }
    return <Navigate to="/blog" replace />
  }

  const index = posts.findIndex((item) => item.slug === post.slug)
  const previous = posts[(index - 1 + posts.length) % posts.length]
  const next = posts[(index + 1) % posts.length]
  const gallery = post.gallery?.length ? post.gallery : DETAIL_GALLERY
  const richContent = (post.content ?? '').trim()
  const hasContent = richContent.replace(/<[^>]*>/g, '').trim().length > 0

  const galleryGrid = (
    <div className="row gy-4 mb-20">
      {gallery.map((image, galleryIndex) => (
        <div className="col-sm-6" key={image}>
          <div className="blog-post-gallary reveal">
            <figure className="image-anime">
              <img src={image} alt={`${post.title} gallery ${galleryIndex + 1}`} />
            </figure>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <>
      <Seo
        override={{
          title: post.metaTitle?.trim() || `${post.title} | ${site.name}`,
          description: post.metaDescription?.trim() || post.excerpt,
          image: post.ogImage?.trim() || post.image || undefined,
          canonical: post.canonicalUrl?.trim() || undefined,
          noIndex: post.noIndex,
          type: 'article',
        }}
      />

      <PageBreadcrumb
        title={post.title}
        items={[{ label: 'Home', to: '/' }, { label: 'Our Blog', to: '/blog' }, { label: post.title }]}
      />

      <section className="blog-single-section pt-100 md-pt-80 pb-100 md-pb-80">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="blog-single-post">
                <div className="blog-block-one">
                  <div className="blog-single-media">
                    <figure className="image-anime">
                      <img src={DETAIL_IMAGE} alt={post.title} />
                    </figure>
                  </div>

                  <div className="blog-single-contain">
                    <div className="blog-entry-content">
                      <ul className="blog-single-meta">
                        <li>
                          <a href="#">
                            <i className="fa-solid fa-user"></i>
                            <span>By: {post.author}</span>
                          </a>
                        </li>
                        <li>
                          <i className="fa-solid fa-calendar-days"></i>
                          <span>{post.date}</span>
                        </li>
                        {(post.categories?.length
                          ? post.categories
                          : post.category
                            ? [{ id: post.category, name: post.category, slug: post.category, parentId: null, path: [] }]
                            : []
                        ).map((category) => (
                          <li key={category.id || category.slug || category.name}>
                            <Link to={`/blog/category/${encodeURIComponent(category.slug || category.name)}`}>
                              <i className="fa-solid fa-folder"></i>
                              <span>{category.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>

                      <h2>{post.title}</h2>

                      {hasContent ? (
                        <>
                          {post.intro ? <p>{post.intro}</p> : null}
                          <div
                            className="blog-rich-content"
                            dangerouslySetInnerHTML={richHtml}
                          />
                          {galleryGrid}
                        </>
                      ) : (
                        <>
                          <p>{post.intro}</p>
                          <p>{FILLER_A}</p>

                          <div className="blog-quote">
                            <div className="blog-quote-icon">
                              <i className="fa-solid fa-quote-right"></i>
                            </div>
                            <p>
                              “The doctors at Fast Aid Hospital explained everything clearly and treated my
                              mother with great care during her emergency. I will always be grateful.” — Rakibul
                              Hasan, Patient
                            </p>
                          </div>

                          <p>{FILLER_B}</p>

                          <h3>Share Your Experience</h3>
                          <p>{FILLER_C}</p>

                          {galleryGrid}

                          <p>{FILLER_D}</p>

                          <h3>Let&apos;s Build a Healthy</h3>
                          <p>{FILLER_E}</p>

                          <div className="check-list mb-30">
                            <ul>
                              {CHECKLIST.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>

                          <div className="row mb-30">
                            <div className="col-12">
                              <div className="blog-post-video">
                                <figure className="image-anime">
                                  <img
                                    src="/assets/images/blog/blog-details/blog-single-video.jpg"
                                    alt="blog video"
                                  />
                                </figure>
                                <div className="blog-video-wapper">
                                  <a
                                    className="video-popup video-play play-center"
                                    href="https://www.youtube.com/watch?v=Y-x0efG1seA"
                                    aria-label="play video"
                                  >
                                    <span className="icon">
                                      <i className="fa-solid fa-play"></i>
                                    </span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>

                          <p>{FILLER_F}</p>
                          <p className="mb-0">{FILLER_G}</p>
                        </>
                      )}
                    </div>

                    <div className="share-links">
                      <div className="row">
                        <div className="col-lg-8 col-md-8 col-12">
                          <div className="share-tag">
                            <span className="share-links-title">Releted Tags</span>
                            <ul className="tagcloud">
                              {post.tags.map((tag) => (
                                <li key={tag}>
                                  <Link to={`/blog/tag/${encodeURIComponent(tag)}`}>{tag}</Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-4 col-12 text-md-end">
                          <div className="share-social">
                            <span className="share-links-title">Social Share</span>
                            <ul className="d-flex justify-content-md-end gap-4">
                              <ShareIcon label="instagram" icon="fa-brands fa-instagram" />
                              <ShareIcon label="facebook" icon="fa-brands fa-facebook-f" />
                              <ShareIcon label="twitter" icon="fa-brands fa-x-twitter" />
                              <ShareIcon label="pinterest" icon="fa-brands fa-pinterest-p" />
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <nav className="navigation post-navigation" aria-label="Posts">
                    <div className="row nav-links">
                      <div className="col-md-6">
                        <div className="nav-previous">
                          <Link to={`/blog/${previous.slug}`} rel="prev">
                            <span className="post-nav-icon">
                              <i className="fa-solid fa-chevron-left"></i>
                              <span className="post-nav-head">Previous Post</span>
                            </span>
                            {previous.title}
                          </Link>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="nav-next">
                          <Link to={`/blog/${next.slug}`} rel="next">
                            <span className="post-nav-icon">
                              <span className="post-nav-head">Next Post</span>
                              <i className="fa-solid fa-chevron-right"></i>
                            </span>
                            {next.title}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </nav>

                  <div className="blog-author">
                    <div className="blog-author-image">
                      <figure className="image-anime">
                        <img src="/assets/images/blog/blog-details/author/blog-author.jpg" alt="blog author" />
                      </figure>
                    </div>
                    <div className="blog-author-content">
                      <h3>Fast Aid Hospital Medical Team</h3>
                      <p>
                        Health articles from the doctors and medical team at Fast Aid Hospital, Chauddagram. Our
                        content is written to help you make informed decisions about your health.
                      </p>
                      <ul className="social-icon">
                        <ShareIcon label="instagram" icon="fa-brands fa-instagram" />
                        <ShareIcon label="facebook" icon="fa-brands fa-facebook-f" />
                        <ShareIcon label="twitter" icon="fa-brands fa-x-twitter" />
                        <ShareIcon label="pinterest" icon="fa-brands fa-pinterest-p" />
                      </ul>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <div className="col-lg-4">
              <BlogSidebar />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
